import React, { memo, useCallback, useEffect, useMemo } from "react";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import _ from "lodash";
import ACLStore from "lib/aclStore";
import useStandardsTableProps from "StandardsTable/hooks/useStandardsTableProps";

// modules
import {
  getLearningCourseStandardsQuery,
  getPublishedStandardsQuery,
} from "../../modules/Queries";
import {
  getSelectedCurriculumId,
  getOrganizationId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";

// components
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { default as StandardsDataGrid } from "StandardsTable";

// constants
import { STANDARD_COLUMNS } from "../../modules/Constants";

// utils
import {
  getPublishedStandardsQueryVariables,
  getStandardEdges,
} from "../../modules/Utils";
import {
  getResolvedMinimalTreeSet,
  getFieldValue,
  getFlattenedNodes,
} from "Assessments/modules/Utils";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import { getAcademicSetupType } from "Teacher/routes/LearningCourses/modules/Utils";
import { getPYPElementSetFromNodes, generateNestedSelectedData } from "Utils";
import { ENABLE_PARENT_STANDARDS_TAGGING } from "Constants/permissionConstants";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";

const _getEdgesFromNodeMap = ({
  nodeMap,
  selectedStandards,
  isParentTaggingEnabled,
}) => {
  return Array.from(nodeMap.values(), ({ rowId, children, columns }) => {
    if (!isParentTaggingEnabled && !_.isEmpty(children)) {
      columns[0].isDisabled = true;
    }

    columns[0].value = getCheckboxValue({
      value: selectedStandards.has(rowId),
    });

    return {
      rowId,
      children,
      columns,
    };
  });
};

const StandardsTable = memo(props => {
  const {
    edges: globalEdges,
    selectedStandards,
    setSelectedStandards,
    selectedStandardSet,
    customResetFilters,
    isData,
    isLoading,
    standardsEdgesRef,
    unitPlan,
    showOnlyUnitSelections,
    fieldKey,
    curriculumProgramType,
    isParentTaggingEnabled,
  } = props;

  const showLoader = isLoading && !isData;

  const edges = useMemo(() => {
    if (!showOnlyUnitSelections) {
      return globalEdges;
    }

    // get unit plan data
    const unitPlanFields = _.get(unitPlan, "allFields", []);
    const unitPlanNodes = getResolvedMinimalTreeSet({
      fields: unitPlanFields,
      uid: fieldKey,
      curriculumType: curriculumProgramType,
    });
    const unitPlanValue = getFieldValue({
      fields: unitPlanFields,
      uid: fieldKey,
    });
    const isPYP = curriculumProgramType === CURRICULUM_TYPE_PYP;
    const filteredUnitPlanNodes = _.filter(
      unitPlanNodes,
      standard =>
        standard?.standardsWrapper?.standardSet?.id === selectedStandardSet &&
        (!isPYP || standard?.depth > 0)
    );
    const nodeSet =
      getPYPElementSetFromNodes({
        nodes: filteredUnitPlanNodes,
        ...(isPYP && { startDepth: 1 }),
      }) || {};
    const nestedNodes = generateNestedSelectedData({
      ...nodeSet,
      value: unitPlanValue,
    });
    const flattenedNodes = getFlattenedNodes({ nodes: nestedNodes });

    return _.map(flattenedNodes, node => ({ node }));
  }, [
    globalEdges,
    unitPlan,
    selectedStandardSet,
    showOnlyUnitSelections,
    fieldKey,
    curriculumProgramType,
  ]);

  useEffect(() => {
    if (showLoader) {
      return;
    }

    if (standardsEdgesRef) {
      standardsEdgesRef.current = _.uniqBy(
        [...standardsEdgesRef.current, ...edges],
        ({ node }) => node?.id
      );
    }
  }, [showLoader, edges, selectedStandardSet]);

  const { columns, nodeMap, properties } = useStandardsTableProps({
    edges,
    columns: STANDARD_COLUMNS,
  });

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectedStandards: new Set(selectedStandards),
        isParentTaggingEnabled,
      });
    },
    [selectedStandards, isParentTaggingEnabled]
  );

  const onCellEdit = useCallback(
    ({ params }) => {
      return handleSelectionChange({
        params,
        selectedRows: selectedStandards,
        setSelectedRows: rows => setSelectedStandards(Array.from(rows)),
      });
    },
    [selectedStandards, setSelectedStandards]
  );

  if (showLoader) {
    return <Loading />;
  }

  return (
    <StandardsDataGrid
      columns={columns}
      nodeMap={nodeMap}
      properties={properties}
    >
      <StandardsDataGrid.Body>
        <StandardsDataGrid.Grid
          resetFilters={customResetFilters}
          getEdgesFromNodeMap={getEdgesFromNodeMap}
          onCellEdit={onCellEdit}
          freezeColumns={2}
        />
      </StandardsDataGrid.Body>
    </StandardsDataGrid>
  );
});

StandardsTable.displayName = "StandardsTable";

const mapStateToProps = state => {
  const orgId = getOrganizationId();
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  const academicSetupType = getAcademicSetupType();
  const isParentTaggingEnabled =
    academicSetupType || ACLStore.can(ENABLE_PARENT_STANDARDS_TAGGING);

  return {
    orgId,
    curriculumProgramId,
    curriculumProgramType,
    isParentTaggingEnabled,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getPublishedStandardsQuery, {
    name: "getPublishedStandards",
    options: ({ selectedStandardSet, ...rest }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPublishedStandardsQueryVariables({
        ...rest,
        id: selectedStandardSet,
      }),
    }),
    skip: ({ selectedStandardSet, showCourseStandards }) =>
      !selectedStandardSet || showCourseStandards,
    props({
      getPublishedStandards: { node, networkStatus },
      ownProps: { showCourseStandards },
    }) {
      const edges = getStandardEdges({ node, showCourseStandards });

      return {
        edges,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  graphql(getLearningCourseStandardsQuery, {
    name: "getLearningCourseStandards",
    options: ({ learningCourseId, selectedStandardSet, ...rest }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: getPublishedStandardsQueryVariables({
          ...rest,
          id: learningCourseId,
          standardSetIds: selectedStandardSet ? [selectedStandardSet] : [],
        }),
      };
    },
    skip: ({ learningCourseId, showCourseStandards }) =>
      !learningCourseId || !showCourseStandards,
    props({
      getLearningCourseStandards: { node, networkStatus },
      ownProps: { showCourseStandards },
    }) {
      const edges = getStandardEdges({ node, showCourseStandards });

      return {
        edges,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  withLoader
)(StandardsTable);
